import { Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";

import DefaultStatisticsCard from "@/components/templates/Cards/StatisticsCards/DefaultStatisticsCard";
import { formatCurrency } from "@/helpers/currency-helpers";
import { getDateRange } from "@/helpers/date-helpers";
import useGetAverageOrderAmount from "@/hooks/requests/dashboard/sales/use-get-average-order-amount";
import { SalesDashboardMessages } from "@/messages/salesDashboard/sales-dashboard.messages";
import { DateRange } from "@/types/date.types";

import { getDateRangeMessages, getDateRangeOptions } from "../constants";

type Props = {
  messages: SalesDashboardMessages;
};

export default function AverageOrderAmountCard({ messages }: Props) {
  const [dateRangeOption, setDateRangeOption] = useState<DateRange>(DateRange.TODAY);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { startDate, endDate } = getDateRange(dateRangeOption);
  const { data, isLoading } = useGetAverageOrderAmount({ startDate, endDate });

  const dateRangeOptions = getDateRangeOptions(messages);
  const dateRangeMessages = getDateRangeMessages(messages);

  const isSmallThan0 = data?.percentageIncrease && data?.percentageIncrease < 0;
  const percentageIncreaseColor = isSmallThan0 ? "error" : "success";

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={menuAnchorEl}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      open={Boolean(menuAnchorEl)}
      onClose={handleMenuClose}
      keepMounted
      disableAutoFocusItem
    >
      {dateRangeOptions.map((option) => (
        <MenuItem
          key={option.value}
          onClick={() => {
            setDateRangeOption(option.value);
            handleMenuClose();
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <DefaultStatisticsCard
      title={messages.averageTicketTitle}
      isLoading={isLoading}
      count={formatCurrency(data?.currentPeriodAverageOrderAmount || 0)}
      percentage={{
        color: percentageIncreaseColor,
        value: `${data?.percentageIncrease}%`,
        label: dateRangeMessages[dateRangeOption] as string,
      }}
      dropdown={{
        action: handleMenuOpen,
        menu: renderMenu,
        value: dateRangeOptions.find((option) => option.value === dateRangeOption)?.label as string,
      }}
    />
  );
}
